import { AnimatePresence, motion } from 'framer-motion'

import { useOutlet, useLocation } from 'react-router-dom'

import { ErrorBoundary } from '@/components/feedback/ErrorBoundary'
import { pageVariants } from '@/lib/motion'

/* Animates the routed page on navigation.
 *
 * Two details do the real work here.
 *
 * First, the key is the PATHNAME only, deliberately excluding the search
 * string. Sections within a record — the case rail, the finance tabs, the
 * directory tabs — are search params rather than nested routes precisely so
 * that switching between them keeps the surrounding chrome mounted (see
 * routes/index.tsx). Keying on the full location would re-run the transition
 * on every tab switch and remount that chrome, throwing away the scroll
 * position and half-typed chat message those routes were shaped to protect.
 *
 * Second, the outlet element is captured with useOutlet() and frozen for the
 * lifetime of each key. This is what makes the exit correct rather than
 * merely present: reading <Outlet /> directly renders the NEW page inside the
 * OUTGOING wrapper the instant the location changes, so the old page's content
 * is swapped for the new page's while the old wrapper is still fading out —
 * the incoming page visibly plays the outgoing animation. Freezing the element
 * per key means the wrapper that is leaving keeps rendering what it was
 * showing when it was told to leave.
 *
 * mode="wait" holds the incoming page until the outgoing one has finished, so
 * the two never overlap and the content underneath never jumps. The cost is
 * that the durations add: see the note on pageVariants about the 240ms floor
 * this puts under every navigation. */
export function PageTransition() {
  const location = useLocation()
  const outlet = useOutlet()

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        variants={pageVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        /* The page column is a flex container whose children rely on it for
           gap and min-width; the wrapper has to reproduce that or every page
           loses its vertical rhythm the moment it is wrapped. */
        className="flex w-full min-w-0 flex-col gap-5"
      >
        {/* Inside the shell, so a page that crashes leaves the navigation, the
            topbar and the connection bar alive — the user can walk to another
            section instead of being left with a blank document and the browser
            back button. Keyed on the pathname for the same reason the
            transition is: navigating away from a crashed page must clear it,
            and the boundary's own state does not know the route changed. */}
        <ErrorBoundary label="page" resetKey={location.pathname}>
          {outlet}
        </ErrorBoundary>
      </motion.div>
    </AnimatePresence>
  )
}
